import {z} from "zod/v4-mini"
import {isInstanceOf} from "@cm/utils"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode} from "@cm/graph-v3"
import {
    registerTemplateGraphNode,
    ObjectNode,
    objectNodeParameters,
    getTemplateNodeReturns,
    proceduralTemplateMarker,
    getGroupPartForNumber,
    getGroupPartNumber,
    isGroupPart,
    isNodeWithMatrixArrayLockedTransformParameter,
    visitNone,
    visit,
    TemplateNode,
    activationVisitorMarker,
} from "@cm/template-nodes"
import type {
    ObjectNodeDefinition,
    ObjectNodeParameters,
    OnCompileContext,
    OnFilterActiveContext,
    TemplateNodeContext,
    VisitorNodeResult,
    ProceduralTemplateNode,
    SceneInteractiveNode,
    INodeSceneInteractionsContext,
    IGetInfoNodeParts,
    IProceduralTemplateContext,
    ObjectPathPart,
    SceneNodePart,
    TemplateNodePart,
    SceneNodes,
    InterfaceDescriptor,
} from "@cm/template-nodes"
import {MattressComponentGraph} from "#mattress/nodes/component/mattress-component-graph"
import {MattressComponentInstance} from "#mattress/nodes/component/mattress-component-instance"
import type {MattressInfoNode} from "#mattress/nodes/mattress-info-node"
import {mattressInfoNode} from "#mattress/nodes/mattress-info-node"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressInfoData, MattressMeshGeometryParametersData} from "#mattress/nodes/component/mattress-component-types"
import type {MattressComponentInterfaceDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {isInterfaceIdPrefix, isNodePathPrefixOf} from "#mattress/nodes/component/mattress-component-helpers"
import {mattressOnCompileImpl} from "#mattress/on-compile/mattress-on-compile"

import {Matrix4} from "@cm/math"

export type MattressParameters = {
    name: string
    root: MattressComponentGraph
} & ObjectNodeParameters

const definition: ObjectNodeDefinition<MattressParameters> = {
    nodeClass: "Mattress",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                root: graphNode(MattressComponentGraph),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class Mattress extends ObjectNode<MattressParameters> implements ProceduralTemplateNode, SceneInteractiveNode {
    get [proceduralTemplateMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    get [activationVisitorMarker]() {
        return true as const
    }

    public override onFilterActive(_context: OnFilterActiveContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressParameters> {
        visit(this.parameters.root, evaluator)
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressParameters> {
        // NOTE visit should not be called directly on mattress parameters since
        // these expect MattressOnCompileContext
        mattressOnCompileImpl(context, this, this.parameters)
        return visitNone(this.parameters)
    }

    getMattressInfoNode(context: IGetInfoNodeParts): MattressInfoNode | null {
        const infoNodes = context.getInfoNodeParts({templateNode: this, part: "root"})
        if (!infoNodes) return null
        if (infoNodes.length !== 1 || !mattressInfoNode.is(infoNodes[0].infoNode)) return null
        return infoNodes[0].infoNode
    }

    getMattressNodeInfoData(node: TemplateNode, context: IGetInfoNodeParts): MattressInfoData | null {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.infoDataList || !infoNode.focusedComponentInstancePath) return null
        const focusedInstancePath = infoNode.focusedComponentInstancePath
        if (focusedInstancePath === "root") {
            return infoNode.infoDataList.find((data) => data.path[0] === node) ?? null
        } else {
            return (
                infoNode.infoDataList.find((data) => isNodePathPrefixOf(focusedInstancePath, data.path) && data.path[focusedInstancePath.length] === node) ??
                null
            )
        }
    }

    getFocusedComponentInstanceWrappedId(context: IGetInfoNodeParts): string | null {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode) return null
        return infoNode.focusedComponentInstanceWrappedId
    }

    getComponentDescriptorsForInstance(instanceId: string, context: IGetInfoNodeParts): MattressComponentInterfaceDescriptor<unknown>[] {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.componentDescriptorList) return []
        return infoNode.componentDescriptorList.filter((descriptor) => isInterfaceIdPrefix(descriptor.props.id, instanceId))
    }

    getTemplateDescriptorsForInstance(instanceId: string, context: IGetInfoNodeParts): InterfaceDescriptor<unknown>[] {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.templateDescriptorList) return []
        return infoNode.templateDescriptorList.filter((descriptor) => isInterfaceIdPrefix(descriptor.props.id, instanceId))
    }

    getMeshGeometryParameters(context: IGetInfoNodeParts): MattressMeshGeometryParametersData | null {
        const infoNode = this.getMattressInfoNode(context)
        return infoNode ? infoNode.meshGeometryParameters : null
    }

    getMeshGeometryParametersSafe(context: IGetInfoNodeParts): MattressMeshGeometryParametersData {
        const meshGeometryParameters = this.getMeshGeometryParameters(context)
        if (!meshGeometryParameters) throw new Error("Evaluated mattress mesh geometry parameters not found")
        return meshGeometryParameters
    }

    getBaseMeshSceneNode(context: IGetInfoNodeParts): SceneNodes.Mesh | null {
        return this.getMattressInfoNode(context)?.baseMeshSceneNode ?? null
    }

    getSegmentedMeshSceneNode(context: IGetInfoNodeParts): SceneNodes.Mesh | null {
        return this.getMattressInfoNode(context)?.segmentedMeshSceneNode ?? null
    }

    getReferenceFrameData(referenceFrame: MattressReferenceFrameLikeNode, context: IGetInfoNodeParts): MattressReferenceFrameData | null {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.referenceFramesDataList) return null
        const focusedInstancePath = infoNode.focusedComponentInstancePath

        const predicate = (data: MattressReferenceFrameData) => {
            if (focusedInstancePath === "root") return data.path.at(0) === referenceFrame
            else return isNodePathPrefixOf(focusedInstancePath, data.path) && data.path.at(focusedInstancePath.length) === referenceFrame
        }

        return infoNode.referenceFramesDataList.find(predicate) ?? null
    }

    getMattressCurveData(curve: MattressCurveLikeNode, context: IGetInfoNodeParts): MattressCurveData | null {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.curvesDataList) return null
        const focusedInstancePath = infoNode.focusedComponentInstancePath

        const predicate = (data: MattressCurveData) => {
            if (focusedInstancePath === "root") return data.path.at(0) === curve
            else return isNodePathPrefixOf(focusedInstancePath, data.path) && data.path.at(focusedInstancePath.length) === curve
        }

        return infoNode.curvesDataList.find(predicate) ?? null
    }

    getTransform(): Matrix4 {
        if (!isNodeWithMatrixArrayLockedTransformParameter(this)) throw new Error("Mattress does not have explicit locked transform")
        const lockedTransform = this.parameters.lockedTransform
        if (lockedTransform === undefined) throw new Error("Mattress transform is undefined")
        return new Matrix4(lockedTransform)
    }

    private getSceneNodesForLocalNode(node: TemplateNode, context: IGetInfoNodeParts) {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.localSceneNodes) return []
        return infoNode.localSceneNodes.filter((x) => x.path.at(0) === this && x.path.some((n) => n.instanceId === node.instanceId))
    }

    getParameterNodePartForSceneNodePart(sceneNodePart: SceneNodePart, context: IProceduralTemplateContext): ObjectPathPart | undefined {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.localNodesDataList) return undefined

        const segmentedMeshSceneNode = infoNode.segmentedMeshSceneNode
        const segmentedMeshMaterialSlotsOwners = infoNode.segmentedMeshMaterialSlotsOwners

        const mattressIdxInPath = sceneNodePart.sceneNode.path.findIndex((node) => node === this)
        if (mattressIdxInPath === -1) return undefined
        const localNodePath = sceneNodePart.sceneNode.path.slice(mattressIdxInPath + 1)
        if (localNodePath.length === 0) return undefined

        const nodePath = (() => {
            if (sceneNodePart.sceneNode === segmentedMeshSceneNode) {
                if (isGroupPart(sceneNodePart.part)) {
                    const groupPartNumber = getGroupPartNumber(sceneNodePart.part)
                    const materialSlotOwnerPath = segmentedMeshMaterialSlotsOwners ? segmentedMeshMaterialSlotsOwners[groupPartNumber] : undefined
                    return materialSlotOwnerPath
                }
            } else {
                const localNode = localNodePath.at(0)
                if (!localNode) return undefined
                const localNodesData = infoNode.localNodesDataList.find((data) => data.nodes.some((x) => x.instanceId === localNode.instanceId))
                if (!localNodesData) return undefined
                return localNodesData.path
            }
            return undefined
        })()
        if (!nodePath) return undefined

        return {path: nodePath, part: sceneNodePart.part}
    }

    getSceneNodePartsForParameterNodePart(parameterNodePart: TemplateNodePart, context: IProceduralTemplateContext): SceneNodePart[] {
        const infoNode = this.getMattressInfoNode(context)
        if (!infoNode || !infoNode.localNodesDataList || !infoNode.localSceneNodes) return []
        const focusedInstancePath = infoNode.focusedComponentInstancePath
        const localNodesDataList = infoNode.localNodesDataList
        const segmentedMeshMaterialSlotsOwners = infoNode.segmentedMeshMaterialSlotsOwners ?? {}
        const segmentedMeshSceneNode = infoNode.segmentedMeshSceneNode

        const sceneNodeParts: SceneNodePart[] = []
        if (segmentedMeshSceneNode) {
            const segmentedMeshMaterialSlotIds = Object.keys(segmentedMeshMaterialSlotsOwners).map((x) => parseInt(x))

            for (const materialSlotId of segmentedMeshMaterialSlotIds) {
                const nodePath = segmentedMeshMaterialSlotsOwners[materialSlotId]
                if (!nodePath) continue

                const nodeIdx = nodePath.findIndex((node) => node === parameterNodePart.templateNode)
                if (nodeIdx === -1) continue

                if (focusedInstancePath === "root") {
                    if (nodeIdx !== 0) continue
                    sceneNodeParts.push({sceneNode: segmentedMeshSceneNode, part: getGroupPartForNumber(materialSlotId)})
                } else {
                    if (!isNodePathPrefixOf(focusedInstancePath, nodePath)) continue
                    else if (nodeIdx === focusedInstancePath.length) {
                        sceneNodeParts.push({sceneNode: segmentedMeshSceneNode, part: getGroupPartForNumber(materialSlotId)})
                    }
                }
            }
        }

        for (const localNodesData of localNodesDataList) {
            const nodePath = localNodesData.path
            const lastNodeInPath = nodePath.at(-1)
            if (lastNodeInPath === undefined) continue
            if (parameterNodePart.templateNode instanceof MattressComponentInstance && !(lastNodeInPath instanceof MattressComponentInstance)) continue

            const nodeIdx = nodePath.findIndex((node) => node === parameterNodePart.templateNode)
            if (nodeIdx === -1) continue

            if (focusedInstancePath === "root") {
                if (nodeIdx !== 0) continue
                sceneNodeParts.push(
                    ...localNodesData.nodes
                        .map((localNode) => this.getSceneNodesForLocalNode(localNode, context).map((sceneNode) => ({sceneNode, part: parameterNodePart.part})))
                        .flat(),
                )
            } else {
                if (!isNodePathPrefixOf(focusedInstancePath, nodePath)) continue
                else if (nodeIdx === focusedInstancePath.length) {
                    sceneNodeParts.push(
                        ...localNodesData.nodes
                            .map((localNode) =>
                                this.getSceneNodesForLocalNode(localNode, context).map((sceneNode) => ({sceneNode, part: parameterNodePart.part})),
                            )
                            .flat(),
                    )
                }
            }
        }

        return sceneNodeParts
    }

    override canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return false
    }

    override canTransformPart(_part: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): boolean {
        return isNodeWithMatrixArrayLockedTransformParameter(this)
    }

    getPartMaterialAssignment(_part: TemplateNodePart["part"]) {
        return undefined
    }
}

export function findClosestParentComponentGraph(node: TemplateNode): MattressComponentGraph {
    const parents = Array.from(node.parents).filter(isInstanceOf(TemplateNode))
    for (const parent of parents) {
        if (parent instanceof MattressComponentGraph) return parent
        if (parent instanceof Mattress) throw new Error(`Failed to find parent component graph for node "${node.nodeClass}"`)
        const result = findClosestParentComponentGraph(parent)
        if (result) return result
    }
    throw new Error(`Failed to find parent component graph for node "${node.nodeClass}"`)
}
